import { useEffect, useMemo, useState } from 'react'
import { translate } from '../../configuration/localization'
import { buildRoleDiff, type HumanRoleId, type RolePresetOption, type StudioUser } from '../../domain/customization/studio'
import { customizationApi } from './application'
import { previewApi, type PreviewRuntimeResult } from './previewApplication'

const roleLabels: Record<HumanRoleId, ReturnType<typeof translate>> = {
  manager_pactlane: translate('role.managerPactlane'),
  manager_agency_creator: translate('role.managerAgencyCreator'),
  creator_talent: translate('role.creatorTalent'),
  brand: translate('role.brand'),
}

export function RoleUserCustomizationStudio({ tenantId }: { tenantId: string }) {
  const [query, setQuery] = useState('')
  const [users, setUsers] = useState<StudioUser[]>([])
  const [presets, setPresets] = useState<RolePresetOption[]>([])
  const [selectedUserId, setSelectedUserId] = useState('')
  const [selectedRole, setSelectedRole] = useState<HumanRoleId>('creator_talent')
  const [selectedVersion, setSelectedVersion] = useState(1)
  const [defaultView, setDefaultView] = useState('')
  const [reason, setReason] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [preview, setPreview] = useState<PreviewRuntimeResult | null>(null)

  const selectedUser = useMemo(() => users.find((item) => item.userId === selectedUserId) ?? null, [selectedUserId, users])
  const selectedPreset = useMemo(() => presets.find((item) => item.roleId === selectedRole && item.version === selectedVersion) ?? null, [presets, selectedRole, selectedVersion])
  const currentPreset = useMemo(() => selectedUser?.humanRoleId
    ? presets.find((item) => item.roleId === selectedUser.humanRoleId && item.version === (selectedUser.rolePresetVersion ?? 1)) ?? null
    : null, [presets, selectedUser])
  const roleDiff = useMemo(() => selectedUser ? buildRoleDiff(selectedUser, selectedRole, selectedVersion) : null, [selectedRole, selectedUser, selectedVersion])

  async function loadPresets() {
    const result = await customizationApi.listRolePresets()
    if (result.error) setError(result.error.message)
    else setPresets(result.data ?? [])
  }

  async function search() {
    setLoading(true)
    setError('')
    setMessage('')
    setPreview(null)
    const result = await customizationApi.searchUsers(tenantId, query)
    if (result.error) {
      setError(result.error.message)
      setUsers([])
    } else {
      const rows = result.data ?? []
      setUsers(rows)
      setSelectedUserId((current) => rows.some((item) => item.userId === current) ? current : rows[0]?.userId ?? '')
    }
    setLoading(false)
  }

  useEffect(() => {
    void loadPresets()
    void search()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId])

  useEffect(() => {
    if (!selectedUser) return
    if (selectedUser.humanRoleId) setSelectedRole(selectedUser.humanRoleId)
    setSelectedVersion(selectedUser.rolePresetVersion ?? 1)
    setDefaultView(selectedUser.defaultView ?? '')
    setReason('')
    setConfirmed(false)
    setPreview(null)
  }, [selectedUser])

  async function applyRole() {
    if (!selectedUser || !roleDiff?.changed || !confirmed) return
    setLoading(true)
    setError('')
    const result = await customizationApi.assignRole({ tenantId, userId: selectedUser.userId, roleId: selectedRole, presetVersion: selectedVersion, reason })
    if (result.error) setError(result.error.message)
    else {
      setMessage(translate('studio.roleUpdated'))
      await search()
    }
    setLoading(false)
  }

  async function applyDefaultView() {
    if (!selectedUser || !defaultView.trim() || !confirmed) return
    setLoading(true)
    setError('')
    const result = await customizationApi.activateDefaultViewOverride({ tenantId, userId: selectedUser.userId, defaultView, reason })
    if (result.error) setError(result.error.message)
    else {
      setMessage(translate('studio.overrideUpdated'))
      await search()
    }
    setLoading(false)
  }

  async function loadPreview() {
    if (!selectedUser?.humanRoleId || !currentPreset) return
    setLoading(true)
    setError('')
    const result = await previewApi.load({
      tenantId,
      userId: selectedUser.userId,
      roleId: selectedUser.humanRoleId,
      capabilities: currentPreset.capabilities,
    })
    if (result.error) {
      setError(result.error.message)
      setPreview(null)
    } else {
      setPreview(result.data)
    }
    setLoading(false)
  }

  return <section id="customization-studio" className="panel">
    <div className="section-heading">
      <div><p className="eyebrow">{translate('studio.eyebrow')}</p><h2>{translate('studio.title')}</h2></div>
      <p className="muted">{translate('studio.subtitle')}</p>
    </div>

    <div className="studio-search-row">
      <label>{translate('studio.searchLabel')}
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={translate('studio.searchPlaceholder')} />
      </label>
      <button type="button" onClick={() => void search()} disabled={loading}>{loading ? translate('studio.loading') : translate('studio.searchAction')}</button>
    </div>

    {error && <div className="notice error" role="alert">{error}</div>}
    {message && <div className="notice" role="status">{message}</div>}

    <div className="studio-layout">
      <div className="studio-results" aria-label={translate('studio.title')}>
        {users.map((user) => <button type="button" key={user.userId} className={user.userId === selectedUserId ? 'studio-user-card active' : 'studio-user-card'} onClick={() => setSelectedUserId(user.userId)}>
          <strong>{user.displayName || user.username || user.userId}</strong>
          <span>@{user.username || translate('studio.noUsername')}</span>
          <small>{user.userId}</small>
          <span>{user.humanRoleId ? roleLabels[user.humanRoleId] : user.legacyRole}</span>
          <span>{translate('studio.defaultView')}: {user.defaultView || translate('studio.fromRole')}</span>
          <span>{translate('studio.modules')}: {user.enabledModules.length ? user.enabledModules.join(', ') : translate('studio.noModules')}</span>
          <span>{user.hasUserOverride ? translate('studio.overrideActive') : translate('studio.roleDefault')}</span>
        </button>)}
        {!loading && users.length === 0 && <p className="muted">{translate('studio.noResults')}</p>}
      </div>

      {selectedUser && <div className="studio-editor">
        <div className="two-column">
          <div>
            <p className="eyebrow">{translate('studio.rolePreset')}</p>
            <label>{translate('studio.role')}
              <select value={selectedRole} onChange={(event) => {
                const role = event.target.value as HumanRoleId
                setSelectedRole(role)
                const first = presets.find((item) => item.roleId === role)
                if (first) setSelectedVersion(first.version)
                setConfirmed(false)
              }}>{[...new Set(presets.map((item) => item.roleId))].map((role) => <option key={role} value={role}>{roleLabels[role]}</option>)}</select>
            </label>
            <label>{translate('studio.presetVersion')}
              <select value={selectedVersion} onChange={(event) => { setSelectedVersion(Number(event.target.value)); setConfirmed(false) }}>
                {presets.filter((item) => item.roleId === selectedRole).map((item) => <option key={`${item.roleId}-${item.version}`} value={item.version}>v{item.version}</option>)}
              </select>
            </label>
          </div>
          <div>
            <p className="eyebrow">{translate('studio.permissionScope')}</p>
            <p>{translate('studio.workspaceScope')}: <code>{tenantId}</code></p>
            <p>{translate('studio.membership')}: <strong>{selectedUser.membershipStatus}</strong></p>
            <p>{translate('studio.capabilities')}:</p>
            <ul className="check-list">{(selectedPreset?.capabilities ?? []).map((capability) => <li key={capability}>{capability}</li>)}</ul>
          </div>
        </div>

        <div className="panel compact-panel">
          <p className="eyebrow">{translate('studio.compare')}</p>
          <div className="studio-diff-grid">
            <div><small>{translate('studio.before')}</small><strong>{selectedUser.humanRoleId ? roleLabels[selectedUser.humanRoleId] : translate('studio.legacyRole')} · v{selectedUser.rolePresetVersion ?? '—'}</strong></div>
            <div><small>{translate('studio.after')}</small><strong>{roleLabels[selectedRole]} · v{selectedVersion}</strong></div>
            <div><small>{translate('studio.override')}</small><strong>{selectedUser.hasUserOverride ? translate('studio.overrideActive') : translate('studio.roleDefault')}</strong></div>
          </div>
        </div>

        <div className="panel compact-panel">
          <p className="eyebrow">{translate('studio.userOverride')}</p>
          <label>{translate('studio.defaultView')}
            <input value={defaultView} onChange={(event) => { setDefaultView(event.target.value); setConfirmed(false) }} placeholder="home / work / campaigns…" />
          </label>
          <p className="muted">{translate('studio.overrideHelp')}</p>
        </div>

        <div className="panel compact-panel">
          <div className="section-heading">
            <p className="eyebrow">{translate('studio.preview')}</p>
            <button type="button" onClick={() => void loadPreview()} disabled={loading || !selectedUser.humanRoleId || !currentPreset}>{translate('studio.previewAction')}</button>
          </div>
          {preview && <div className="preview-explainability" aria-live="polite">
            <p><strong>{translate('studio.previewPurpose')}:</strong> <code>{preview.preview.purpose}</code></p>
            <p><strong>{translate('studio.previewScope')}:</strong> <code>{preview.preview.objectScope.workspaceId} · {preview.preview.objectScope.objectType}</code></p>
            <p><strong>{translate('studio.previewNavigation')}:</strong> {preview.preview.navigation.join(' · ') || '—'}</p>
            <p><strong>{translate('studio.modules')}:</strong> {preview.preview.modules.join(' · ') || '—'}</p>
            <p><strong>{translate('studio.previewHome')}:</strong> {preview.preview.homeSections.join(' · ') || '—'}</p>
            <p><strong>{translate('studio.defaultView')}:</strong> {preview.preview.defaultView || '—'}</p>
            <p><strong>Pinned metric:</strong> {preview.preview.pinnedMetric || '—'}</p>
            <div>
              <strong>{translate('studio.previewActions')}:</strong>
              <ul className="check-list">{preview.preview.actions.map((action) => <li key={action.id}>{action.id}: {action.decision} · {action.reasonCode}</li>)}</ul>
            </div>
            <div>
              <strong>{translate('studio.previewFields')}:</strong>
              <ul className="check-list">{preview.fieldVisibility.map((field) => <li key={`${field.objectType}:${field.field}`}>{field.objectType}.{field.field} · {field.visibility}</li>)}</ul>
            </div>
            <div>
              <strong>{translate('studio.previewProvenance')}:</strong>
              <ul className="check-list">{Object.entries(preview.preview.provenance).map(([key, source]) => <li key={key}>{key}: {source}</li>)}</ul>
            </div>
            <div>
              <strong>{translate('studio.previewWarnings')}:</strong>
              {preview.preview.warnings.length
                ? <ul className="check-list">{preview.preview.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
                : <p className="muted">{translate('studio.previewNoWarnings')}</p>}
            </div>
          </div>}
        </div>

        <label>{translate('studio.reason')}
          <textarea value={reason} onChange={(event) => { setReason(event.target.value); setConfirmed(false) }} rows={3} placeholder={translate('studio.reasonPlaceholder')} />
        </label>
        <label className="confirmation-row"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /> {translate('studio.confirm')}</label>
        <div className="top-actions">
          <button type="button" onClick={() => void applyRole()} disabled={loading || !confirmed || !roleDiff?.changed || reason.trim().length < 3}>{translate('studio.updateRole')}</button>
          <button type="button" onClick={() => void applyDefaultView()} disabled={loading || !confirmed || !defaultView.trim() || reason.trim().length < 3}>{translate('studio.activateOverride')}</button>
        </div>
      </div>}
    </div>
  </section>
}
